import { property, subclass } from "@arcgis/core/core/accessorSupport/decorators";
import { tsx, storeNode, messageBundle } from "@arcgis/core/widgets/support/widget";
import Widget from '@arcgis/core/widgets/Widget';
import Collection from "@arcgis/core/core/Collection";
import { whenTrueOnce, whenFalse, watch, init, whenTrue } from "@arcgis/core/core/watchUtils";
import Handles from "@arcgis/core/core/Handles";

import "./Onboarding/styles/Onboarding.scss";
import { VNode } from 'src/interfaces/interfaces';

import AppT9N from "../../t9n/App/App.json";

// Img Imports
import Onboard_A from "../../../public/assets/onboarding/Onboarding_9_2_A.png";
import Onboard_B from "../../../public/assets/onboarding/Onboarding_9_2_B.png";
import Onboard_C from "../../../public/assets/onboarding/Onboarding_9_1_C.png";
import Onboard_D from "../../../public/assets/onboarding/Onboarding_9_2_C.png";
import { isDarkMode } from '../utils/appUtils';


export interface IOnboardingItem {
  id: string;
  title: string;
  content: string;
  img: { src: string; alt: string; };
}

const CSS = {
  base: "esri-config-panel__onboarding",
  onboardingGraphic: "esri-config-panel__onboarding-graphic",
  onboardingDesc: "esri-config-panel__onboarding-desc",
  onboardingPagination: "esri-config-panel__onboarding-pagination",
  labelContainer: "esri-config-panel__onboarding-label-container",
  doNotShowLabel: "esri-config-panel__onboarding-do-not-show-label",
  paginationItemSelected:
    "esri-config-panel__onboarding-pagination-item--selected",
  paginationItem: "esri-config-panel__onboarding-pagination-item"
};

const ONBOARDING_LIVE_REGION_KEY = "onboarding-live-region-key";


@subclass("Landing.widgets.Onboarding")
export default class Onboarding extends Widget {

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  messages: typeof AppT9N;

  @property()
  isOpen = false;

  @property()
  onboardingContentCollection: Collection<IOnboardingItem> = new Collection();

  @property()
  releaseTag: string = null;

  @property({
    readOnly: true
  })
  readonly currentIndex: number = 0;

  @property({
    readOnly: true
  })
  readonly liveRegionContent: { content: string };

  private _handles = new Handles();
  private _ariaLiveRegion = document.getElementById("ariaLiveRegion");

  constructor(params?: any) {
    super(params);
  }

  postInitialize() {
    const { onboardingStrings } = this.messages;
    this.onboardingContentCollection.addMany([
      {
        id: "welcome",
        title: onboardingStrings.welcomeHeader,
        content: onboardingStrings.requirePanel,
        img: {
          src: Onboard_A,
          alt: ""
        }
      },
      {
        id: "preview",
        title: onboardingStrings.previewHeader,
        content: onboardingStrings.previewPanel,
        img: {
          src: Onboard_B,
          alt: ""
        }
      },
      // {
      //   id: "search",
      //   title: onboardingStrings.searchHeader,
      //   content: onboardingStrings.searchPanel,
      //   img: {
      //     src: Onboard_C,
      //     alt: ""
      //   }
      // },
      {
        id: "search",
        title: onboardingStrings.myAppsHeader,
        content: onboardingStrings.myAppsPanel,
        img: {
          src: Onboard_D,
          alt: ""
        }
      },
    ]);

    this._handleOnboardingLR();
  }

  render() {
    const content = this._renderOnboardingContent();
    const onboardingItem = this._getOnboardingItem(this.currentIndex);
    return (
      <calcite-modal
        bind={this}
        class={CSS.base}
        aria-label={onboardingItem?.title}
        afterCreate={(modal: HTMLCalciteModalElement)=>{
          modal.addEventListener("calciteModalClose", ()=>{
            this.close();
          });
        }}
        width="m"
        noPadding={true}
        active={this.isOpen}
        intl-close={this.messages.onboardingStrings.close}
      >
        {content}
      </calcite-modal>
    );
  }

  destroy() {
    this._handles.removeAll();
    this._handles.destroy();
    this._handles = null;
  }

  open(): void {
    this.isOpen = true;
    this._set("currentIndex", 0);
    this.scheduleRender();
  }

  next(): void {
    if (this.currentIndex === this.onboardingContentCollection.length - 1) {
      this._set("currentIndex", 0);
    } else {
      this._set("currentIndex", this.currentIndex + 1);
    }
    this._handleFocus();
    this.scheduleRender();
  }

  select(e: Event): void {
    const node = e.target as HTMLButtonElement;
    const itemIndex = node.getAttribute("data-item-index");
    this._set("currentIndex", parseInt(itemIndex));
    this.scheduleRender();
  }

  close(): void {
    this.isOpen = false;
    this.scheduleRender();
  }

  // Start of render methods
  private _renderOnboardingContent(): VNode {
    const modalHeader = this._renderModalHeader();
    const modalContent = this._renderModalContent();
    const { nextText, doneText } = this.messages.onboardingStrings;
    return [
      modalHeader,
      modalContent,

      <calcite-button
        key="next-close-button"
        bind={this}
        id="onboardingButton"
        onclick={
          this.currentIndex !== this.onboardingContentCollection.length - 1
            ? this.next.bind(this)
            : this.close.bind(this)
        }
        slot="primary"
        width="full"
      >
        {this.currentIndex !== this.onboardingContentCollection.length - 1
          ? nextText
          : doneText}
      </calcite-button>
    ];
  }

  private _renderModalHeader(): VNode {
    const onboardingItem = this._getOnboardingItem(this.currentIndex);
    return (
      <h2 slot="header" id="modalTitle">
        {onboardingItem?.title}
      </h2>
    );
  }

  private _renderModalContent(): VNode {
    const onboardingItem = this._getOnboardingItem(this.currentIndex);
    return (
      <div slot="content">
        <img
          class={CSS.onboardingGraphic}
          src={onboardingItem?.img?.src}
          alt={onboardingItem?.img?.alt}
        />
        <div class={CSS.onboardingDesc}>
          <p>{onboardingItem?.content}</p>
        </div>
        {this._renderPaginationItemContainer()}
      </div>
    );
  }

  private _renderPaginationItemContainer(): VNode {
    return (
      <div class={CSS.onboardingPagination}>
        {this._renderPaginationItems()}
      </div>
    );
  }

  private _renderPaginationItems(): VNode[] {
    return this.onboardingContentCollection
      .toArray()
      .map((onboardingItem, onboardingItemIndex) => {
        return this._renderOnboardingPaginationItem(
          onboardingItem,
          onboardingItemIndex
        );
      });
  }

  private _renderOnboardingPaginationItem(
    onboardingItem: IOnboardingItem,
    onboardingItemIndex: number
  ): VNode {
    const selected = {
      [CSS.paginationItemSelected]:
        this.currentIndex ===
        this.onboardingContentCollection.indexOf(onboardingItem)
    };
    return (
      <button
        bind={this}
        onclick={this.select}
        class={this.classes(CSS.paginationItem, selected)}
        data-item-index={`${onboardingItemIndex}`}
        aria-pressed={
          this.currentIndex ===
            this.onboardingContentCollection.indexOf(onboardingItem)
            ? "true"
            : "false"
        }
        aria-label={`${onboardingItemIndex + 1}/${this.onboardingContentCollection.length}`}
      />
    );
  }
  // End of render methods

  private _getOnboardingItem(selectedIndex: number): IOnboardingItem {
    return this.onboardingContentCollection.getItemAt(selectedIndex);
  }

  private _handleFocus(): void {
    const onboardingButtonInt = setInterval(() => {
      const onboardingButton = document.getElementById(
        "onboardingButton"
      ) as HTMLCalciteButtonElement;
      if (onboardingButton) {
        onboardingButton.setFocus();
      }
      if (document.activeElement === onboardingButton) {
        clearInterval(onboardingButtonInt);
      }
    }, 50);
  }

  // ONBOARDING LIVE REGION
  private _handleOnboardingLR(): void {
    this._handles.add(
      [
        this._handleOnboardingOpenLR(),
        this._handleOnboardingClosedLR(),
        this._watchLRContent()
      ],
      ONBOARDING_LIVE_REGION_KEY
    );
  }

  private _handleOnboardingOpenLR(): __esri.WatchHandle {
    return whenTrueOnce(this, "isOpen", () => {
      const currentOnboardingItem = this._getOnboardingItem(this.currentIndex);
      this._set("liveRegionContent", {
        content: currentOnboardingItem.content
      });
      this._handles.add(
        watch(this, "currentIndex", () => {
          const currentOnboardingItem = this._getOnboardingItem(
            this.currentIndex
          );
          this._set("liveRegionContent", {
            content: currentOnboardingItem.content
          });
        })
      );
    });
  }

  private _handleOnboardingClosedLR(): __esri.WatchHandle {
    return whenFalse(this, "isOpen", () => {
      this._set("liveRegionContent", null);
      this._ariaLiveRegion.innerText = "";
    });
  }

  private _watchLRContent(): __esri.WatchHandle {
    return init(this, "liveRegionContent", () => {
      if (this.liveRegionContent) {
        this._ariaLiveRegion.innerText = this.liveRegionContent.content;
      }
    });
  }
}
